import React, { useCallback, useMemo, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { ProfileRestrictionContext } from '@/components/ProfileRestriction/context';
import { useProfileStatus } from '@/hooks/useProfileStatus';
import { ProfileStatus } from '@/types/user';

interface Props {
  children: ReactNode;
}

export const ProfileRestrictionProvider: React.FC<Props> = ({ children }) => {
  const { t } = useTranslation();
  const status = useProfileStatus();
  const [isAlertOpen, setIsAlertOpen] = useState(false);

  const isRestricted =
    status === ProfileStatus.PastStudyYear ||
    status === ProfileStatus.InactiveProfile;

  const guardAction = useCallback(
    (action: () => void) => {
      if (isRestricted) {
        setIsAlertOpen(true);
        return;
      }

      action();
    },
    [isRestricted],
  );

  const value = useMemo(
    () => ({ isRestricted, guardAction }),
    [isRestricted, guardAction],
  );

  return (
    <ProfileRestrictionContext.Provider value={value}>
      {children}
      <ConfirmDialog
        isOpen={isAlertOpen}
        hideCancel
        title={t('profileStatus.restrictedAction.title')}
        message={t('profileStatus.restrictedAction.description')}
        onClose={() => setIsAlertOpen(false)}
        onConfirm={() => setIsAlertOpen(false)}
      />
    </ProfileRestrictionContext.Provider>
  );
};
